import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { incomesAPI, companiesAPI } from '../lib/api';
import { formatCurrency, formatDate, getIncomeCategoryLabel } from '../lib/utils';
import { CurrencySelect } from '../components/shared/CurrencySelect';
import { toast } from 'sonner';
import { TrendingUp, Plus, Trash2, Loader2, CalendarIcon, Filter } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';
import { cn } from '../lib/utils';

const incomeCategories = [
  { value: 'sales', label: 'Sales' },
  { value: 'service', label: 'Service' },
  { value: 'investment', label: 'Investment' },
  { value: 'rental', label: 'Rental Income' },
  { value: 'other', label: 'Other' }
];

export default function Incomes() {
  const [incomes, setIncomes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [formData, setFormData] = useState({ company_id: '', amount: '', currency: 'TRY', category: '', description: '', date: new Date() });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchData(); }, [selectedCompany]);

  const fetchData = async () => {
    try {
      const [incomesRes, companiesRes] = await Promise.all([
        incomesAPI.getAll(selectedCompany !== 'all' ? selectedCompany : undefined), companiesAPI.getAll()
      ]);
      setIncomes(incomesRes.data); setCompanies(companiesRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      await incomesAPI.create({ ...formData, amount: parseFloat(formData.amount), date: formData.date.toISOString() });
      toast.success('Income added'); setDialogOpen(false); resetForm(); fetchData();
    } catch { toast.error('Operation failed'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    try { await incomesAPI.delete(id); toast.success('Income deleted'); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  const resetForm = () => { setFormData({ company_id: '', amount: '', currency: 'TRY', category: '', description: '', date: new Date() }); };
  const getCompanyName = (companyId) => { const company = companies.find(c => c.id === companyId); return company?.name || 'Unknown'; };
  const totalIncome = incomes.reduce((sum, income) => sum + income.amount, 0);

  if (loading) { return (<Layout title="Incomes"><div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></Layout>); }

  return (
    <Layout title="Incomes">
      <div className="space-y-6" data-testid="incomes-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div><h2 className="font-heading text-2xl font-bold">Incomes</h2><p className="text-muted-foreground">Track all your income</p></div>
          <div className="flex items-center gap-3">
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="w-[200px]" data-testid="income-company-filter"><Filter className="h-4 w-4 mr-2" /><SelectValue placeholder="Filter Company" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Companies</SelectItem>{companies.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
            </Select>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild><Button className="rounded-full" data-testid="add-income-btn"><Plus className="h-4 w-4 mr-2" />Add Income</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Add New Income</DialogTitle><DialogDescription>Enter income details</DialogDescription></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2"><Label>Company *</Label>
                    <Select value={formData.company_id} onValueChange={(v) => setFormData({ ...formData, company_id: v })} required>
                      <SelectTrigger data-testid="income-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                      <SelectContent>{companies.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Category *</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })} required>
                      <SelectTrigger data-testid="income-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>{incomeCategories.map((cat) => (<SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2 space-y-2"><Label htmlFor="amount">Amount *</Label><Input id="amount" type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="1000.00" required data-testid="income-amount-input" /></div>
                    <div className="space-y-2"><Label>Currency</Label><CurrencySelect value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })} testId="income-currency-select" /></div>
                  </div>
                  <div className="space-y-2"><Label htmlFor="description">Description *</Label><Input id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Product sales" required data-testid="income-description-input" /></div>
                  <div className="space-y-2"><Label>Date *</Label>
                    <Popover><PopoverTrigger asChild><Button variant="outline" className={cn("w-full justify-start text-left font-normal", !formData.date && "text-muted-foreground")} data-testid="income-date-picker"><CalendarIcon className="mr-2 h-4 w-4" />{formData.date ? format(formData.date, "PPP") : "Select date"}</Button></PopoverTrigger>
                      <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={formData.date} onSelect={(date) => setFormData({ ...formData, date: date || new Date() })} initialFocus /></PopoverContent>
                    </Popover>
                  </div>
                  <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button type="submit" disabled={submitting} data-testid="income-submit-btn">{submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add</Button></div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Total Income</p><p className="text-3xl font-heading font-bold text-emerald-500 mt-1">{formatCurrency(totalIncome)}</p></div><div className="h-14 w-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center"><TrendingUp className="h-7 w-7 text-emerald-500" /></div></div></CardContent></Card>

        <Card>
          <CardHeader><CardTitle className="font-heading">Income List</CardTitle></CardHeader>
          <CardContent>
            {incomes.length > 0 ? (
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Company</TableHead><TableHead>Category</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="w-[50px]"></TableHead></TableRow></TableHeader>
                <TableBody>{incomes.map((income) => (
                  <TableRow key={income.id} data-testid={`income-row-${income.id}`}>
                    <TableCell>{formatDate(income.date)}</TableCell><TableCell>{getCompanyName(income.company_id)}</TableCell>
                    <TableCell><Badge variant="secondary">{getIncomeCategoryLabel(income.category)}</Badge></TableCell>
                    <TableCell className="max-w-[200px] truncate">{income.description}</TableCell>
                    <TableCell className="text-right font-medium text-emerald-500">{formatCurrency(income.amount, income.currency || 'TRY')}</TableCell>
                    <TableCell><Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive" onClick={() => handleDelete(income.id)} data-testid={`delete-income-${income.id}`}><Trash2 className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-4"><TrendingUp className="h-8 w-8 text-emerald-500" /></div>
                <h3 className="font-heading text-lg font-semibold mb-2">No income yet</h3><p className="text-muted-foreground mb-4">Get started by adding your first income</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
